// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {useSudo} from '@app/features/auth/hooks/useSudo';
import Authentication from '@app/features/auth/state/Authentication';
import {startSsoLink} from '@app/features/auth/state/AuthFlow';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {isAbortError} from '@app/features/platform/utils/PlatformHelpers';
import {Button} from '@app/features/ui/button/Button';
import {Input} from '@app/features/ui/components/form/FormInput';
import {navigateToExternalURL} from '@app/features/ui/utils/NativeUtils';
import Users from '@app/features/user/state/Users';
import {ValidationErrorCodes} from '@fluxer/constants/src/ValidationErrorCodes';
import {WalletIcon} from '@phosphor-icons/react';
import {useState} from 'react';

export function DaoIWalletGate({preRuntime = false}: {preRuntime?: boolean}) {
	const sudo = useSudo();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [password, setPassword] = useState('');
	const bind = async () => {
		const userId = Authentication.currentUserId ?? Users.currentUser?.id;
		if (busy || !userId || (preRuntime && !password)) return;
		setBusy(true);
		setError(null);
		try {
			const verification = preRuntime ? {password} : await sudo.require();
			const result = await startSsoLink({
				verification,
				runtimeSnapshot: RuntimeConfig.getSnapshot(),
				linkTargetUserId: userId,
				redirectTo: RouterUtils.getCurrentPath(),
			});
			sudo.finalize();
			await navigateToExternalURL(result.authorizationUrl);
		} catch (failure) {
			if (!isAbortError(failure)) {
				const body = (failure as {body?: {errors?: Array<{code?: string}>}})?.body;
				setError(
					body?.errors?.some((entry) => entry.code === ValidationErrorCodes.INVALID_PASSWORD)
						? '聊天账号密码不正确。请输入此账号的登录密码，而非钱包解锁密码。'
						: '账号验证未完成，暂时无法打开钱包绑定。请稍后重试。',
				);
			}
		} finally {
			setBusy(false);
		}
	};
	return (
		<main className="daoi-gate" data-flx="daoi.dao-i-wallet-gate.daoi-gate">
			<div className="daoi-gate-icon" data-flx="daoi.dao-i-wallet-gate.daoi-gate-icon">
				<WalletIcon size={36} data-flx="daoi.dao-i-wallet-gate.wallet-icon" />
			</div>
			<h1 data-flx="daoi.dao-i-wallet-gate.h1">绑定你的钱包</h1>
			<p data-flx="daoi.dao-i-wallet-gate.p">完成当前账号的钱包签名绑定后，即可进入 DAOI。</p>
			<p className="daoi-muted" data-flx="daoi.dao-i-wallet-gate.daoi-muted">
				一个账号对应一个钱包。连接和断开钱包不会改变绑定关系。
			</p>
			{preRuntime && (
				<div className="daoi-gate-password" data-flx="daoi.dao-i-wallet-gate.daoi-gate-password">
					<Input
						label="确认账号密码"
						type="password"
						value={password}
						autoComplete="current-password"
						onChange={(event) => setPassword(event.target.value)}
						placeholder="输入此聊天账号的登录密码"
						data-flx="daoi.dao-i-wallet-gate.input.set-password"
					/>
				</div>
			)}
			<Button
				fitContainer
				submitting={busy}
				disabled={busy || (preRuntime && !password)}
				onClick={bind}
				data-flx="daoi.dao-i-wallet-gate.daoi-button.bind"
			>
				{busy ? '正在打开钱包…' : '连接并绑定钱包'}
			</Button>
			{error && (
				<p className="daoi-error" role="alert" data-flx="daoi.dao-i-wallet-gate.daoi-error">
					{error}
				</p>
			)}
		</main>
	);
}
